/**
 * section-operations.tsx — Mục "Giờ làm & kênh" của màn nhân viên AI: nhóm "Vận hành — hiệu lực ngay" (không qua phát hành).
 *   - Bật nhân viên AI (gộp Trực tuyến: tắt = ngoại tuyến). Nhân viên AI mặc định tắt được — cảnh báo kênh chưa giao sẽ không có AI.
 *   - Kênh phụ trách (`BotChannelsField`, dùng chung với luồng tạo mới).
 *   - Giờ làm & chế độ (`WorkHoursField`, dùng chung): 2 mẫu nhanh, tuỳ chỉnh, một chế độ cả ngày.
 *   - Số khách mỗi ngày (`maxConcurrentDaily`).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { BotChannelsField } from '@/components/ai/bot-channels-field'
import { WorkHoursField } from '@/components/ai/work-hours-field'
import type { ScheduleValue } from '@/components/ai/work-hours-lib'
import type { SectionProps } from '../train/draft-form'
import { StaffPanel, SubBlock } from './staff-ui'
import { enabledPatch, isOn } from './staff-lib'

export function OperationsSection({ botId, form, set, canEdit, isDefault, intakeToday }: SectionProps & {
  isDefault: boolean
  intakeToday?: number
}) {
  const { t } = useTranslation('aiStaff')
  const on = isOn(form)
  const schedule: ScheduleValue = {
    scheduleEnabled: form.scheduleEnabled,
    scheduleStartHour: form.scheduleStartHour,
    scheduleEndHour: form.scheduleEndHour,
    scheduleDaytimeMode: form.scheduleDaytimeMode,
    scheduleNighttimeMode: form.scheduleNighttimeMode,
    defaultMode: form.defaultMode,
  }

  const toggle = (v: boolean) => {
    if (!v && isDefault && !window.confirm(t('operations.confirmDisableDefault'))) return
    set(enabledPatch(v, form.onlineStatus))
  }

  return (
    <StaffPanel
      id="staff-gio-lam-kenh" title={t('operations.title')} icon={<CalendarClock className="h-4 w-4" />} scope="live"
      description={t('operations.description')}
    >
      <SubBlock
        id="staff-van-hanh" title={t('operations.enable.title')}
        description={isDefault
          ? t('operations.enable.descDefault')
          : t('operations.enable.desc')}
        actions={
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            {on ? t('operations.enable.on') : t('operations.enable.off')}
            <Switch checked={on} onCheckedChange={toggle} disabled={!canEdit} aria-label={t('operations.enable.title')} />
          </label>
        }
      >
        {form.enabled && form.onlineStatus === 'busy' && <p className="text-xs text-muted-foreground">{t('operations.enable.busy')}</p>}
      </SubBlock>

      <SubBlock id="staff-kenh" title={t('operations.channels.title')} description={t('operations.channels.description')}>
        <BotChannelsField botId={botId} isDefault={isDefault} value={form.channelAccountIds} onChange={(ids) => set({ channelAccountIds: ids })} disabled={!canEdit} />
      </SubBlock>

      <SubBlock id="staff-gio-lam" title={t('operations.hours.title')} description={t('operations.hours.description')}>
        <WorkHoursField value={schedule} onChange={(v) => set(v)} disabled={!canEdit} />
      </SubBlock>

      <SubBlock id="staff-so-khach" title={t('operations.daily.title')} description={t('operations.daily.description')}>
        <MaxDailyInput value={form.maxConcurrentDaily} onChange={(n) => set({ maxConcurrentDaily: n })} disabled={!canEdit} intakeToday={intakeToday} />
      </SubBlock>
    </StaffPanel>
  )
}

function MaxDailyInput({ value, onChange, disabled, intakeToday }: { value: number; onChange: (n: number) => void; disabled: boolean; intakeToday?: number }) {
  const { t } = useTranslation('aiStaff')
  const [text, setText] = useState(String(value))
  const [seen, setSeen] = useState(value)
  if (seen !== value) { setSeen(value); setText(String(value)) }
  const n = Number(text)
  const bad = !/^\d+$/.test(text.trim()) || n < 1 || n > 9999
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Input
        type="number" min={1} max={9999} inputMode="numeric" className="h-8 w-28" value={text} disabled={disabled}
        aria-label={t('operations.daily.ariaLabel')}
        onChange={(e) => {
          setText(e.target.value)
          const v = Number(e.target.value)
          if (/^\d+$/.test(e.target.value.trim()) && v >= 1 && v <= 9999) onChange(v)
        }}
      />
      <span className="text-muted-foreground">{t('operations.daily.unit')}</span>
      {intakeToday !== undefined && <span className="text-xs font-medium">{t('operations.daily.today', { n: intakeToday, total: value })}</span>}
      {bad && <p className="w-full text-xs text-destructive">{t('operations.daily.invalid')}</p>}
    </div>
  )
}
